import { alpha, SxProps, Theme } from '@mui/material/styles';

// Page-level styles
export const paperSx: SxProps<Theme> = (theme: Theme) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  marginBottom: theme.spacing(2),
});

export const sectionTitleSx: SxProps<Theme> = (theme: Theme) => ({
  marginBottom: theme.spacing(2),
});

// Contribution Form styles
export const percentageInputBoxSx: SxProps<Theme> = {
  width: '180px',
};

export const sectionSpacingSx: SxProps<Theme> = (theme: Theme) => ({
  marginTop: theme.spacing(4),
});

export const calculationRowSx: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
});

// TipShareSettingsPage styles
export const pageContainerSx = {
  ml: 4,
  mt: 3,
};

export const headerSectionSx: SxProps<Theme> = (theme: Theme) => ({
  mt: 3,
});

export const loadingContainerSx: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'center',
  mt: 4,
});

export const formSectionSx: SxProps<Theme> = (theme: Theme) => ({
  mt: 3,
});

export const contributionSectionSx: SxProps<Theme> = (theme: Theme) => ({
  mt: 2,
});

export const buttonContainerSx: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'center',
  p: 2,
});

export const shiftsSectionSx: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
});

export const shiftsSectionHeaderSx: SxProps<Theme> = {
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 5,
};

export const shiftsSectionHeaderContentSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const createShiftButtonSx: SxProps<Theme> = {
  ml: 'auto',
  flexShrink: 0,
};

export const getFullDayCoverageWarningSx =
  (showWarning: boolean): SxProps<Theme> =>
  (theme: Theme) => ({
    mt: 1,
    color: theme.palette.error.main,
    visibility: showWarning ? 'visible' : 'hidden',
  });

export const tipShareAmountTitleSx: SxProps<Theme> = (theme: Theme) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1),
});

// Distribution Form styles
export const radioGroupContainerSx: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
});

export const nestedRadioGroupSx: SxProps<Theme> = (theme: Theme) => ({
  marginLeft: theme.spacing(4),
  marginTop: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
});

export const netSalesRowSx: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
});

// Additional reusable inline styles moved from components
export const rowFlexWithCenterAlignmentNoGapSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 0,
};

export const rowFlexWithCenterAlignmentSmallGapSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 0.5,
};

export const formControlLabelMarginBottomSx: SxProps<Theme> = (theme: Theme) => ({
  marginBottom: theme.spacing(2),
});

export const rowFlexWithCenterAlignmentMediumGapSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 2,
};

export const switchToAutoDialogSx: SxProps<Theme> = (theme: Theme) => ({
  '& .MuiDialogTitle-root': {
    padding: theme.spacing(3, 3, 2, 3),
    ...theme.typography.h6,
    fontWeight: 600,
  },
  '& .MuiDialogContentText-root': {
    ...theme.typography.body1,
    fontSize: '17px',
  },
  '& .MuiDialogContent-root': {
    paddingBottom: theme.spacing(2.5),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1, 3, 3, 3),
  },
  '& .MuiDialogActions-root .MuiButton-root': {
    ...theme.typography.button,
  },
});

export const rowFlexWithCenterAlignmentSmallMarginSx: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
  marginBottom: theme.spacing(2),
});

export const tooltipWhiteSpacePreLineSx: SxProps<Theme> = {
  whiteSpace: 'pre-line',
};

export const rowFlexWithLargeGapSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  gap: 3,
};

export const calculationBreakdownMaxWidthSx: SxProps<Theme> = {
  maxWidth: '400px',
};

// Drawer styles
export const drawerContentBoxSx: SxProps<Theme> = (theme: Theme) => ({
  width: '100%',
  pl: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
});

export const drawerTitleSx: SxProps<Theme> = (theme: Theme) => ({
  mb: theme.spacing(2),
  mt: 0,
});

// Dialog styles
export const dialogActionsSx: SxProps<Theme> = (theme: Theme) => ({
  p: theme.spacing(2),
});

export const shiftsGridTimeCellContentSx: SxProps<Theme> = {
  width: '100%',
  pr: 1,
  py: 0.5,
  textAlign: 'left',
};

export const shiftsGridWarningHeaderContentSx: SxProps<Theme> = (theme: Theme) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(0.75),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(0.5),
});

export const shiftsGridWarningHeaderIconSx: SxProps<Theme> = {
  fontSize: 16,
  flexShrink: 0,
};

export const getShiftsGridIncompleteCoverageSx = (headerClassName: string, cellClassName: string): SxProps<Theme> => ({
  [`& .MuiDataGrid-columnHeader.${headerClassName}`]: {
    backgroundColor: (theme: Theme) =>
      `${alpha(theme.palette.error.light, theme.palette.mode === 'light' ? 0.12 : 0.1)} !important`,
  },
  [`& .MuiDataGrid-cell.${cellClassName}`]: {
    backgroundColor: (theme: Theme) =>
      `${alpha(theme.palette.error.light, theme.palette.mode === 'light' ? 0.12 : 0.1)} !important`,
  },
  [`& .MuiDataGrid-row:hover .MuiDataGrid-cell.${cellClassName}`]: {
    backgroundColor: (theme: Theme) =>
      `${alpha(theme.palette.error.light, theme.palette.mode === 'light' ? 0.12 : 0.1)} !important`,
  },
});
